import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import type { LegalDocument } from '../../data/legal';
const EASE = [0.23, 1, 0.32, 1] as const;
type LegalModalProps = {
  document: LegalDocument | null;
  onClose: () => void;
};
export function LegalModal({
  document: doc,
  onClose
}: LegalModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const reduceMotion = useReducedMotion();
  const isOpen = doc !== null;
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const previousOverflow = window.document.body.style.overflow;
    window.document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    closeRef.current?.focus();
    return () => {
      window.document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);
  return <AnimatePresence data-id="element-38" data-mp="2u30djjwqyie">
      {doc && <div className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-6" data-id="element-39" data-mp="7upot3gubvwd">
          <motion.div className="absolute inset-0 bg-ink/80" initial={reduceMotion ? false : {
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={reduceMotion ? undefined : {
        opacity: 0
      }} transition={{
        duration: 0.18,
        ease: EASE
      }} onClick={onClose} aria-hidden="true" data-id="element-40" data-mp="3q0c5zo9s022w" />

          <motion.div role="dialog" aria-modal="true" aria-labelledby={`legal-${doc.id}-title`} initial={reduceMotion ? false : {
        opacity: 0,
        scale: 0.96,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={reduceMotion ? undefined : {
        opacity: 0,
        scale: 0.98,
        y: 8
      }} transition={{
        duration: 0.24,
        ease: EASE
      }} className="relative flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-xl border border-gold/25 bg-cocoa sm:rounded-xl" data-id="element-41" data-mp="qkfghhpb41k2">
            <div className="flex items-start justify-between gap-6 border-b border-white/10 px-6 py-5 sm:px-8" data-id="element-42" data-mp="1f5tyneja437r">
              <div data-id="element-43" data-mp="1a5pffgqzkudu">
                <h2 id={`legal-${doc.id}-title`} className="font-display text-2xl text-cream sm:text-3xl" data-id="element-44" data-mp="1a55x3yxfc3za">
                  {doc.title}
                </h2>
                <p className="mt-1 text-xs uppercase tracking-[0.16em] text-sand/70" data-id="element-45" data-mp="118f5pztthdb1">
                  {doc.updated}
                </p>
              </div>
              <button ref={closeRef} type="button" onClick={onClose} aria-label="Cerrar" className="-mr-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sand transition-colors duration-150 ease-out hover:bg-white/5 hover:text-cream focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/50" data-id="element-46" data-mp="rhkhwr0xa5nh">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-47" data-mp="2p3jxhjack44l" />
              </button>
            </div>

            <div className="overflow-y-auto px-6 py-6 sm:px-8" data-id="element-48" data-mp="2t43u8lr0d5q7">
              <p className="text-sand" data-id="element-49" data-mp="2y4i2o4wiuhor">{doc.intro}</p>

              <div className="mt-8 space-y-7" data-id="element-50" data-mp="2y2oxyv5gy7je">
                {doc.sections.map(section => <section key={section.heading} data-id="element-51" data-mp="331ryc2ai7pl1">
                    <h3 className="font-display text-xl text-gold-soft" data-id="element-52" data-mp="25h5g3uqd29vg">{section.heading}</h3>
                    <div className="mt-2 space-y-3" data-id="element-53" data-mp="2aefg24bdm3xj">
                      {section.paragraphs.map(paragraph => <p key={paragraph} className="text-sm leading-relaxed text-sand" data-id="element-54" data-mp="2fdi9erhzulpo">
                          {paragraph}
                        </p>)}
                    </div>
                  </section>)}
              </div>
            </div>

            <div className="border-t border-white/10 px-6 py-4 sm:px-8" data-id="element-55" data-mp="1rnnwyb1l6z5y">
              <button type="button" onClick={onClose} className="w-full rounded-full border border-gold/40 px-6 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-gold transition-colors duration-150 ease-out hover:bg-gold hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/50" data-id="element-56" data-mp="3tdlls6u1votu">
                Cerrar
              </button>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}